/**
 * SentryContextSync
 *
 * Keeps Sentry's user + workspace tags in sync with the app's stores.
 * Mounted once at the root of <App /> so every route benefits.
 *
 * Why this lives here (not inside userStore):
 *   - keeps the Zustand stores free of observability-library imports
 *   - naturally handles login, logout, workspace switching, and tab-restored
 *     state without any manual call sites
 */

import { useEffect } from 'react';
import { useUserStore } from '../stores/prioritization/userStore';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import {
  clearSentryUser,
  setSentryContext,
  setSentryWorkspace,
} from '../lib/sentry';
import { clearUserFlagsState, loadUserFlags } from '../lib/featureFlags';

const UNKNOWN_USER_ID = 'unknown';

export function SentryContextSync(): null {
  const userId = useUserStore((s) => s.userId);
  const userEmail = useUserStore((s) => s.userEmail);
  const currentRole = useUserStore((s) => s.currentRole);
  const assignedRoleNames = useUserStore((s) => s.assignedRoleNames);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaces = useTenantStore((s) => s.workspaces);

  // Resolve role: prefer assigned role name (from DB), fall back to currentRole.
  const role = assignedRoleNames?.[0] ?? currentRole ?? null;

  // Resolve workspace name from the in-memory list.
  const workspace =
    currentWorkspaceId
      ? workspaces.find((w) => w.id === currentWorkspaceId)
      : undefined;

  // Push full user + workspace context once both are known.
  useEffect(() => {
    if (!userId || userId === UNKNOWN_USER_ID) {
      clearSentryUser();
      return;
    }
    if (!currentWorkspaceId || !workspace) {
      return;
    }
    setSentryContext(
      { id: userId, email: userEmail || undefined },
      { id: workspace.id, name: workspace.name },
      role
    );
  }, [userId, userEmail, role, currentWorkspaceId, workspace]);

  // Load user-scoped feature flags as soon as we know who the user is.
  // Keyed only on userId — does not depend on workspace resolution, so flags
  // are ready before the first render of workspace-aware components.
  useEffect(() => {
    if (!userId || userId === UNKNOWN_USER_ID) {
      clearUserFlagsState();
      return;
    }
    void loadUserFlags(userId);
  }, [userId]);

  // Handle workspace-only changes (e.g. user switches workspace mid-session).
  useEffect(() => {
    if (!userId || userId === UNKNOWN_USER_ID) return;
    if (!workspace) return;
    setSentryWorkspace({ id: workspace.id, name: workspace.name });
  }, [userId, workspace?.id, workspace?.name]);

  return null;
}
